import {AbstractAccountKeyring, AbstractPeerAccountKeyring, AbstractSelfAccountKeyring} from '../keyring/account_base';
import { AbstractRoomKeyring } from '../keyring/room_base';
import { TimestampWindowCalculator } from './timestamp';
import {HKDFKeyGeneratorV1, HKDFKeyGeneratorV2} from '../base/key/kdf';
import { Curve25519SharedSecretGenerator } from '../base/key/dh';
import {cmpByteArray, concatByteArray} from '../utility';
import {MissingKey} from "../keyring/base";

// TODOIMP
export let TS_WINDOW_V1 = new TimestampWindowCalculator(60 * 60 * 24 * 14, 2 * 60 * 60); // 2 weeks with 2 hour overlap

/**
 * Freeze the HKDF construct for account secret and salt usage.
 */
export class AccountMasterDeriveKeyV1 {
  public static derive_key(
    self_kr: AbstractSelfAccountKeyring,
    info: Uint8Array,
    cache: boolean = true,
    size: number = 64
  ): Promise<Uint8Array> {
    return HKDFKeyGeneratorV1.new_key(
      size,
      self_kr.get_secret_key(AbstractSelfAccountKeyring.MASTER),
      concatByteArray(HKDFKeyGeneratorV1.SALTS[0], self_kr.id),
      info
    );
  }
}

/**
 * Freeze the HKDF construct for account secret and salt usage.
 */
export class AccountMasterDeriveKeyV2 {
  public static derive_key(
    self_kr: AbstractSelfAccountKeyring,
    info: Uint8Array,
    cache: boolean = true,
    size: number = 64
  ): Promise<Uint8Array> {
    return HKDFKeyGeneratorV2.new_key(
      size,
      self_kr.get_secret_key(AbstractSelfAccountKeyring.MASTER),
      concatByteArray(HKDFKeyGeneratorV2.SALTS[2], self_kr.id),
      info
    );
  }
}

/**
 * Freeze the HKDF construct for account-pair shared-secret and salt usage.
 */
export class AccountPairDeriveKeyV1 {
  public static derive_key(
    self_kr: AbstractSelfAccountKeyring,
    peer_kr: AbstractAccountKeyring,
    info: Uint8Array,
    cache: boolean = true
  ): Promise<Uint8Array> {
    return HKDFKeyGeneratorV1.new_key(
      64,
      Curve25519SharedSecretGenerator.new_key(
        self_kr.get_secret_key(AbstractSelfAccountKeyring.CURVE25519),
        peer_kr.get_public_key(AbstractSelfAccountKeyring.CURVE25519)
      ),
      concatByteArray(HKDFKeyGeneratorV1.SALTS[0], self_kr.ordered_ids(peer_kr)),
      info
    );
  }
}

/**
 * Freeze the HKDF construct for account-pair shared-secret and salt usage.
 */
export class AccountPairDeriveKeyV2 {
  public static derive_key(
    shared_secret: Uint8Array,
    self_kr: AbstractSelfAccountKeyring,
    peer_kr: AbstractAccountKeyring,
    info: Uint8Array,
    cache: boolean = true
  ): Promise<Uint8Array> {
    return HKDFKeyGeneratorV2.new_key(
      64,
      concatByteArray(
        Curve25519SharedSecretGenerator.new_key(
          self_kr.get_secret_key(AbstractSelfAccountKeyring.CURVE25519),
          peer_kr.get_public_key(AbstractSelfAccountKeyring.CURVE25519)
        ),
        shared_secret
      ),
      concatByteArray(HKDFKeyGeneratorV2.SALTS[2], self_kr.ordered_ids(peer_kr)),
      info
    );
  }
}

/**
 * Freeze the HKDF construct for room-pinned key and salt usage.
 */
export class RoomPinnedDeriveKeyV1 {
  public static derive_key(
    room_kr: AbstractRoomKeyring,
    info: Uint8Array,
    cache: boolean = true
  ): Promise<Uint8Array> {
    return HKDFKeyGeneratorV1.new_key(
      64,
      room_kr.get_key(AbstractRoomKeyring.PINNED),
      concatByteArray(HKDFKeyGeneratorV1.SALTS[0], room_kr.id),
      info
    );
  }
}

export class KemSharedSecretSelectorV1 {
  public static select_ss_for_load(
    self_kr: AbstractSelfAccountKeyring,
    peer_kr: AbstractPeerAccountKeyring,
    version: Uint8Array,
    other_version: Uint8Array
  ): Uint8Array {
    return peer_kr.get_shared_secret(
      cmpByteArray(peer_kr.id, self_kr.id) < 0 === cmpByteArray(version, other_version) < 0
        ? AbstractPeerAccountKeyring.SS_SELF_MLKEM1024
        : AbstractPeerAccountKeyring.SS_PEER_MLKEM1024
    );
  }

  public static select_ss_version_for_dump(
    self_kr: AbstractSelfAccountKeyring,
    peer_kr: AbstractPeerAccountKeyring,
    version: Uint8Array,
    other_version: Uint8Array
  ): { shared_secret: Uint8Array; selected_version: Uint8Array } {
    let shared_secret: Uint8Array;
    let selected_version: Uint8Array;
    try {
      shared_secret = peer_kr.get_shared_secret(AbstractPeerAccountKeyring.SS_SELF_MLKEM1024);
      selected_version =
        cmpByteArray(peer_kr.id, self_kr.id) < 0 === cmpByteArray(version, other_version) < 0
          ? version
          : other_version;
    } catch (err) {
      if (err instanceof MissingKey) {
      } else {
        throw err;
      }
      shared_secret = peer_kr.get_shared_secret(AbstractPeerAccountKeyring.SS_PEER_MLKEM1024);
      selected_version =
        cmpByteArray(peer_kr.id, self_kr.id) < 0 === cmpByteArray(version, other_version) < 0
          ? other_version
          : version;
    }
    return { shared_secret, selected_version };
  }
}
